/**
 * Settings → Integrations → Adapty, a webhook URL per Adapty app: the form
 * rules, kept out of the component so they are testable.
 *
 * One Adapty app covers both stores, so it links up to one iOS and one
 * Android catalogue row. A row already given its own URL by another Adapty
 * app can't be linked again (the server refuses it too).
 */
import type { AdaptyWebhookApp, AdaptyWebhookAppSave, App } from "./types";

export type AndroidChoice =
  | { kind: "none" }
  | { kind: "existing"; appId: number }
  | { kind: "new"; name: string; bundleId: string };

export type AdaptyAppForm = {
  name: string;
  iosAppId: number | null;
  android: AndroidChoice;
};

export function formFor(app: AdaptyWebhookApp | null): AdaptyAppForm {
  return {
    name: app?.name ?? "",
    iosAppId: app?.ios?.app_id ?? null,
    android: app?.android ? { kind: "existing", appId: app.android.app_id } : { kind: "none" },
  };
}

export function suggestedAndroidName(name: string): string {
  const n = name.trim();
  return n ? `${n} (Android)` : "";
}

/** Active catalogue rows of each platform the form may link: not linked to
 *  another Adapty app. The edited app's own rows stay selectable. */
export function linkableApps(
  apps: App[], webhookApps: AdaptyWebhookApp[], editingId: number | null,
): { ios: App[]; android: App[] } {
  const taken = new Set<number>();
  for (const w of webhookApps) {
    if (w.id === editingId) continue;
    if (w.ios) taken.add(w.ios.app_id);
    if (w.android) taken.add(w.android.app_id);
  }
  const free = apps.filter((a) => a.is_active && !taken.has(a.id));
  return {
    ios: free.filter((a) => a.platform === "ios"),
    android: free.filter((a) => a.platform === "android"),
  };
}

/** The first problem with the form, or null when it can be saved. */
export function validateForm(form: AdaptyAppForm): string | null {
  if (!form.name.trim()) return "Name the Adapty app.";
  if (form.iosAppId == null && form.android.kind === "none") {
    return "Link an iOS or an Android app — events need somewhere to go.";
  }
  if (form.android.kind === "new") {
    if (!form.android.name.trim()) return "Name the new Android app.";
    if (!form.android.bundleId.trim()) return "Enter the Android package name (e.g. com.company.app).";
  }
  return null;
}

export function toPayload(form: AdaptyAppForm): AdaptyWebhookAppSave {
  const a = form.android;
  return {
    name: form.name.trim(),
    ios_app_id: form.iosAppId,
    android_app_id: a.kind === "existing" ? a.appId : null,
    new_android: a.kind === "new" ? { name: a.name.trim(), bundle_id: a.bundleId.trim() } : null,
  };
}

export function sameForm(a: AdaptyAppForm, b: AdaptyAppForm): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Where an app's own URL stands: receiving its events (green), only the
 *  Save probe from Adapty arrived (set up, no event yet), or nothing yet. */
export type UrlStatus =
  | { kind: "receiving"; at: string }
  | { kind: "confirmed"; at: string }
  | { kind: "waiting" };

export function urlStatus(app: AdaptyWebhookApp): UrlStatus {
  if (app.last_webhook_at) return { kind: "receiving", at: app.last_webhook_at };
  if (app.verified_at) return { kind: "confirmed", at: app.verified_at };
  return { kind: "waiting" };
}

export function urlStatusText(s: UrlStatus, fmt: (iso: string) => string): string {
  switch (s.kind) {
    case "receiving":
      return `Last data received: ${fmt(s.at)}`;
    case "confirmed":
      return `Test event received — OK (${fmt(s.at)}). Waiting for the first event.`;
    case "waiting":
      return "Not connected yet — paste the URL into the app’s webhook settings in Adapty and press Save.";
  }
}

/** Refetch cadence for the per-app list: every 15 s while any URL hasn't
 *  received an event yet — the user is likely setting it up in Adapty in
 *  another tab — then once a minute. */
export function pollMs(apps: AdaptyWebhookApp[]): number {
  return apps.some((a) => urlStatus(a).kind !== "receiving") ? 15_000 : 60_000;
}

/** The same status in a word or two, for the Adapty tile's app list. */
export function urlStatusShort(s: UrlStatus, fmt: (iso: string) => string): string {
  switch (s.kind) {
    case "receiving":
      return `Receiving data · ${fmt(s.at)}`;
    case "confirmed":
      return `Test event OK · ${fmt(s.at)}`;
    case "waiting":
      return "Not connected";
  }
}
